import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import { useDialogue } from './DialogueContext';

const RecordingContext = createContext();

export const RecordingProvider = ({ children, getAuthHeader, user }) => {
  const { currentDialogueId, setCurrentDialogueId, setCurrentDialogueName, loadDialogues } = useDialogue();
  const [messages, setMessages] = useState([]);
  const [pendingTransactions, setPendingTransactions] = useState([]);
  const [newIssueIds, setNewIssueIds] = useState(new Set());
  const [currentContext, setCurrentContext] = useState(null);
  const [progressUpdates, setProgressUpdates] = useState({});
  const socketRef = useRef(null);

  // Cache in-progress messages per dialogue to restore them when switching back
  const inProgressMessagesCache = useRef({});

  // Ref to always hold the latest currentDialogueId (for use after awaits)
  const currentDialogueIdRef = useRef(currentDialogueId);
  currentDialogueIdRef.current = currentDialogueId;

  // Shared sendAudio function used by all components
  const sendAudio = useCallback(async (audioBlob, context = null, retryId = null) => {
    if (!audioBlob) {
      const errorMsg = {
        id: Date.now() + '-err',
        sender: 'bot',
        text: 'Recording too short or failed. Please try recording again.',
        ts: new Date(),
      };
      setMessages(list => [...list, errorMsg]);
      return { success: false, error: 'Recording too short or failed' };
    }

    const ts = new Date();
    const id = retryId || ts.getTime();
    const url = URL.createObjectURL(audioBlob);

    // Add or update audio message to history
    setMessages(m => {
      if (retryId) {
        // For retry, update existing message
        return m.map(msg =>
          msg.id === retryId
            ? { ...msg, uploadFailed: false, audioBlob }
            : msg
        );
      } else {
        // For new upload, add message with audioBlob stored for retry
        return [...m, {
          id,
          sender: 'me',
          audioUrl: url,
          ts,
          context: context || currentContext,
          audioBlob // Store for retry
        }];
      }
    });

    // Add processing message
    setMessages(list => [
      ...list,
      {
        id: `${id}-bot`,
        sender: 'bot',
        parts: ['', '', ''],
        text: '',
        steps: [{ id: 'upload', label: 'Uploading audio', status: 'active', startTime: Date.now() }],
        ts: new Date(),
      },
    ]);

    const ext = 'webm';
    const form = new FormData();
    form.append('audio', audioBlob, `voice-${id}.${ext}`);

    // Add context if available (like issue key for Jira)
    if (context?.issueKey || currentContext?.issueKey) {
      const ctx = context || currentContext;
      const contextText = `Issue ID: ${ctx.issueKey}\n${ctx.summary || ''}\n\n`;
      form.append('text', contextText);
    }

    const storedAiMode = localStorage.getItem('aiMode') || 'speed';
    form.append('aiMode', storedAiMode);

    const MAX_WHISPER_BYTES = 25 * 1024 * 1024;
    if (audioBlob.size > MAX_WHISPER_BYTES) {
      // Delete the bot message
      setMessages(list => list.filter(m => m.id !== `${id}-bot`));

      // Mark user message as failed
      setMessages(m => m.map(msg =>
        msg.id === id
          ? { ...msg, uploadFailed: true, errorMessage: `Audio too large (${(audioBlob.size/1024/1024).toFixed(1)} MB). Please keep recordings shorter.` }
          : msg
      ));

      return { success: false, error: 'Audio too large' };
    }

    try {
      const doFetch = () => fetch(`/api/transcribe?id=${id}&dialogueId=${currentDialogueId || ''}`, {
        method: 'POST',
        body: form,
        headers: { Authorization: getAuthHeader() }
      });

      let res = await doFetch();

      if (res.status === 401) {
        const body = await res.json().catch(() => null);
        if (body?.error === 'GOOGLE_TOKEN_EXPIRED') {
          // Handle token refresh
          await new Promise(resolve => {
            window.google.accounts.id.initialize({
              client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
              auto_select: true,
              callback: ({ credential }) => {
                if (credential) {
                  window.location.reload();
                }
                resolve();
              },
            });
            window.google.accounts.id.prompt();
          });
          res = await doFetch();
        }
      }

      const data = await res.json();

      // If backend created a new dialogue, update current dialogue ID
      if (data.dialogueId && !currentDialogueId) {
        setCurrentDialogueId(data.dialogueId);
        localStorage.setItem('lastDialogueId', data.dialogueId);
        console.log(`[Dialogue] New dialogue created: ${data.dialogueId}`);
      }

      // Update the bot message with the actual response
      if (data.answer || data.transcript) {
        setMessages(list => {
          const idx = list.findIndex(m => m.id === `${id}-bot`);
          if (idx === -1) return list;

          const copy = [...list];
          copy[idx] = {
            ...copy[idx],
            text: data.answer || data.transcript,
            parts: undefined // Clear the parts to show just the text
          };
          return copy;
        });
      }

      // Handle transactions if present — only if still in the same dialogue
      const stillSameDialogue = currentDialogueIdRef.current === currentDialogueId
        || (currentDialogueIdRef.current === data.dialogueId);
      if (stillSameDialogue) {
        if (data.transactions && data.transactions.length > 0) {
          setPendingTransactions(data.transactions);
          setNewIssueIds(new Set(data.newIssueIds || []));
        } else {
          setPendingTransactions([]);
          setNewIssueIds(new Set());
        }
      } else {
        console.log(`[sendAudio] Dialogue changed during request (was ${currentDialogueId}, now ${currentDialogueIdRef.current}). Skipping transaction update.`);
      }

      return {
        success: true,
        data,
        transcript: data.answer || data.transcript,
        transactions: data.transactions || []
      };
    } catch (err) {
      console.error('Transcription error:', err);

      // Delete the bot message
      setMessages(list => list.filter(m => m.id !== `${id}-bot`));

      // Mark user message as failed
      setMessages(m => m.map(msg =>
        msg.id === id
          ? { ...msg, uploadFailed: true, errorMessage: 'Failed to process recording. Please try again.' }
          : msg
      ));

      return { success: false, error: 'Failed to process recording' };
    }
  }, [currentContext, getAuthHeader, currentDialogueId, setCurrentDialogueId]);

  const commitTransactions = useCallback(async () => {
    try {
      // Get the most recent dialogueId from localStorage as fallback
      const dialogueId = currentDialogueId || localStorage.getItem('lastDialogueId');

      const res = await fetch('/api/transactions/commit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({ dialogueId })
      });

      const data = await res.json();
      if (data.success) {
        setPendingTransactions([]);
        setNewIssueIds(new Set());
        // Return with idMap for ID replacement
        return {
          success: true,
          data,
          idMap: data.results?.idMap || {}
        };
      } else {
        return { success: false, error: data.message || 'Unknown error' };
      }
    } catch (err) {
      console.error('Transaction commit error:', err);
      return { success: false, error: 'Error applying changes' };
    }
  }, [getAuthHeader, currentDialogueId]);

  const cancelTransactions = useCallback(async () => {
    try {
      // Get the most recent dialogueId from localStorage as fallback
      const dialogueId = currentDialogueId || localStorage.getItem('lastDialogueId');

      const res = await fetch('/api/transactions/cancel', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: getAuthHeader()
        },
        body: JSON.stringify({ dialogueId })
      });

      const data = await res.json();
      if (data.success) {
        setPendingTransactions([]);
        setNewIssueIds(new Set());
        return { success: true, data };
      } else {
        return { success: false, error: data.message || 'Unknown error' };
      }
    } catch (err) {
      console.error('Transaction cancel error:', err);
      return { success: false, error: 'Error cancelling transactions' };
    }
  }, [getAuthHeader, currentDialogueId]);

  const retryUpload = useCallback(async (messageId) => {
    // Find the message with the audioBlob
    const message = messages.find(m => m.id === messageId);
    if (!message || !message.audioBlob) {
      console.error('Cannot retry: message or audioBlob not found');
      return { success: false, error: 'Audio not available for retry' };
    }

    // Retry the upload with the same ID
    return await sendAudio(message.audioBlob, message.context, messageId);
  }, [messages, sendAudio]);

  // Initialize socket connection
  useEffect(() => {
    if (!user) return;

    const socket = io("/", { 
      auth: { 
        token: user.sessionToken || user.idToken,
        type: user.useSession ? 'session' : 'google'
      } 
    });
    socketRef.current = socket;

    socket.on("progress", ({ id, phase, data, dialogueId }) => {
      // Update messages for ChatWindow compatibility
      setMessages(list => {
        if (!Array.isArray(list)) return list;
        let idx = list.findIndex(m => m.id === `${id}-bot`);

        // If message not found, create it for the current dialogue
        if (idx === -1 && dialogueId && dialogueId === currentDialogueIdRef.current) {
          const newMsg = {
            id: `${id}-bot`,
            sender: 'bot',
            parts: ['', '', ''],
            text: '',
            steps: [],
            ts: new Date()
          };
          list = [...list, newMsg];
          idx = list.length - 1;
          console.log(`[Progress] Created bot message ${id}-bot for current dialogue ${dialogueId}`);
        }

        // If message not found in current dialogue, this might be for a different dialogue
        if (idx === -1) {
          // Check if we should create a cached message for another dialogue
          if (dialogueId && dialogueId !== currentDialogueIdRef.current) {
            console.log(`[Progress] Caching event for background dialogue ${dialogueId}`);

            // Initialize or get cached messages for this dialogue
            if (!inProgressMessagesCache.current[dialogueId]) {
              inProgressMessagesCache.current[dialogueId] = [];
            }

            // Find or create the message in cache
            let cachedIdx = inProgressMessagesCache.current[dialogueId].findIndex(m => m.id === `${id}-bot`);
            let msg;

            if (cachedIdx === -1) {
              // Create new cached message
              msg = {
                id: `${id}-bot`,
                sender: 'bot',
                parts: ['', '', ''],
                text: '',
                ts: new Date()
              };
              inProgressMessagesCache.current[dialogueId].push(msg);
              cachedIdx = inProgressMessagesCache.current[dialogueId].length - 1;
            } else {
              msg = { ...inProgressMessagesCache.current[dialogueId][cachedIdx] };
            }

            // Update the cached message
            msg.parts = msg.parts || [];
            while (msg.parts.length < 3) msg.parts.push('');

            switch (phase) {
              case 'recognition_started':
                // Mark upload done, add transcription step
                msg.steps = (msg.steps || []).map(s =>
                  s.id === 'upload' ? { ...s, status: 'done', elapsedMs: Date.now() - (s.startTime || Date.now()) } : s
                );
                msg.steps.push({ id: 'recognition', label: 'Transcribing audio', status: 'active', startTime: Date.now() });
                break;
              case 'recognition_done':
                msg.parts[0] = `You said: "${data.text}"`;
                msg.steps = (msg.steps || []).map(s =>
                  s.id === 'recognition' ? { ...s, status: 'done', elapsedMs: data.elapsedMs || (s.startTime ? Date.now() - s.startTime : undefined) } : s
                );
                msg.steps.push({ id: 'classify', label: 'Planning actions', status: 'active', startTime: Date.now() });
                break;
              case 'command_detect_started':
                if (!msg.steps || msg.steps.length === 0) {
                  msg.steps = [{ id: 'classify', label: 'Planning actions', status: 'active', startTime: Date.now() }];
                }
                break;
              case 'classify_started':
                if (!msg.steps?.find(s => s.id === 'classify')) {
                  msg.steps = [...(msg.steps || []), { id: 'classify', label: 'Planning actions', status: 'active', startTime: Date.now() }];
                }
                break;
              case 'classify_done':
                msg.steps = (msg.steps || []).map(s =>
                  s.id === 'classify' ? { ...s, status: 'done', elapsedMs: data.elapsedMs } : s
                );
                break;
              case 'llm_start': {
                const loop = data.loop || 1;
                const label = loop === 1 ? 'Thinking' : 'Processing results';
                msg.steps = (msg.steps || []).map(s =>
                  s.id?.startsWith('llm_') && s.status === 'active' ? { ...s, status: 'done' } : s
                );
                msg.steps.push({ id: `llm_${loop}`, label, status: 'active', startTime: Date.now() });
                break;
              }
              case 'llm_done': {
                const llmElapsed = data.elapsedMs;
                msg.steps = (msg.steps || []).map(s =>
                  s.id === `llm_${data.loop}` && s.status === 'active' ? { ...s, status: 'done', elapsedMs: llmElapsed } : s
                );
                break;
              }
              case 'tool_start': {
                msg.steps = (msg.steps || []).map(s =>
                  s.id?.startsWith('llm_') && s.status === 'active' ? { ...s, status: 'done' } : s
                );
                const tools = data.tools || [];
                for (const t of tools) {
                  msg.steps.push({ id: `tool_${t.name}_${Date.now()}`, label: t.label || t.name, status: 'active', startTime: Date.now() });
                }
                break;
              }
              case 'tool_done': {
                const elapsed = data.elapsedMs;
                const doneNames = new Set((data.tools || []).map(t => t.name));
                msg.steps = (msg.steps || []).map(s => {
                  if (s.status !== 'active') return s;
                  const match = s.id?.match(/^tool_(.+)_\d+$/);
                  if (match && doneNames.has(match[1])) {
                    return { ...s, status: 'done', elapsedMs: elapsed };
                  }
                  return s;
                });
                break;
              }
              case 'tool': {
                const names = data.names || [data.name];
                msg.steps = (msg.steps || []).map(s =>
                  s.id?.startsWith('llm_') && s.status === 'active' ? { ...s, status: 'done' } : s
                );
                for (const name of names) {
                  msg.steps = [...(msg.steps || [])];
                  msg.steps.push({ id: `tool_${name}_${Date.now()}`, label: name, status: 'active', startTime: Date.now() });
                }
                break;
              }
              case 'agent_stream':
                // Live streaming from Claude Code agent
                if (!msg.steps?.find(s => s.id === 'agent')) {
                  msg.steps = (msg.steps || []).map(s =>
                    s.status === 'active' ? { ...s, status: 'done' } : s
                  );
                  msg.steps.push({ id: 'agent', label: 'Agent working', status: 'active', startTime: Date.now() });
                }
                msg.parts[2] = data.text;
                msg.text = msg.parts.filter(Boolean).join('\n');
                break;
              case 'llm_delta':
                // Streaming OpenAI Responses completion — accumulated text so far.
                msg.parts[2] = data.accum || '';
                msg.text = msg.parts.filter(Boolean).join('\n');
                break;
              case 'final':
                msg.steps = (msg.steps || []).map(s =>
                  s.status === 'active' ? { ...s, status: 'done' } : s
                );
                msg.totalElapsedMs = data.totalElapsedMs;
                msg.parts[1] = '';
                msg.parts[2] = data.text;
                if (data.messageId) {
                  msg.backendMessageId = data.messageId;
                }
                msg.text = msg.parts.filter(Boolean).join('\n');
                inProgressMessagesCache.current[dialogueId][cachedIdx] = msg;

                // Remove from cache after a delay to allow Firestore save to complete
                setTimeout(() => {
                  if (inProgressMessagesCache.current[dialogueId]) {
                    inProgressMessagesCache.current[dialogueId] =
                      inProgressMessagesCache.current[dialogueId].filter(m => m.id !== msg.id);
                    console.log(`[Progress] Removed completed message ${msg.id} from cache for dialogue ${dialogueId}`);
                  }
                }, 2000);
                return; // Don't update the cached message again below
              case 'error':
                msg.steps = (msg.steps || []).map(s =>
                  s.status === 'active' ? { ...s, status: 'failed' } : s
                );
                msg.parts[1] = '';
                msg.parts[2] = data.message || 'An error occurred. Please try again.';
                msg.text = msg.parts.filter(Boolean).join('\n');
                inProgressMessagesCache.current[dialogueId][cachedIdx] = msg;

                // Remove from cache after a delay
                setTimeout(() => {
                  if (inProgressMessagesCache.current[dialogueId]) {
                    inProgressMessagesCache.current[dialogueId] =
                      inProgressMessagesCache.current[dialogueId].filter(m => m.id !== msg.id);
                    console.log(`[Progress] Removed error message ${msg.id} from cache for dialogue ${dialogueId}`);
                  }
                }, 2000);
                return; // Don't update the cached message again below
              case 'command_detect_done':
                msg.steps = (msg.steps || []).map(s =>
                  s.id === 'classify' ? { ...s, status: 'done' } : s
                );
                break;
              default:
                break;
            }

            // Build parts[1] from steps for text display (fallback)
            const bgActiveSteps = (msg.steps || []).filter(s => s.status === 'active');
            msg.parts[1] = bgActiveSteps.length > 0 ? bgActiveSteps.map(s => s.label).join(', ') + '...' : '';
            msg.text = msg.parts.filter(Boolean).join('\n');
            inProgressMessagesCache.current[dialogueId][cachedIdx] = msg;
          }

          return list; // Don't modify current messages
        }

        const copy = [...list];
        const msg = { ...copy[idx] };

        msg.parts = msg.parts || [];
        while (msg.parts.length < 3) msg.parts.push('');

        switch (phase) {
          case 'recognition_started':
            // Mark upload done, add transcription step
            msg.steps = (msg.steps || []).map(s =>
              s.id === 'upload' ? { ...s, status: 'done', elapsedMs: Date.now() - (s.startTime || Date.now()) } : s
            );
            msg.steps.push({ id: 'recognition', label: 'Transcribing audio', status: 'active', startTime: Date.now() });
            break;
          case 'recognition_done':
            msg.parts[0] = `You said: "${data.text}"`;
            msg.steps = (msg.steps || []).map(s =>
              s.id === 'recognition' ? { ...s, status: 'done', elapsedMs: data.elapsedMs || (s.startTime ? Date.now() - s.startTime : undefined) } : s
            );
            msg.steps.push({ id: 'classify', label: 'Planning actions', status: 'active', startTime: Date.now() });
            break;
          case 'command_detect_started':
            if (!msg.steps || msg.steps.length === 0) {
              msg.steps = [{ id: 'classify', label: 'Planning actions', status: 'active', startTime: Date.now() }];
            }
            break;
          case 'classify_started':
            // Mark any existing classify step as active, or add one
            if (!msg.steps?.find(s => s.id === 'classify')) {
              msg.steps = [...(msg.steps || []), { id: 'classify', label: 'Planning actions', status: 'active', startTime: Date.now() }];
            }
            break;
          case 'classify_done':
            msg.steps = (msg.steps || []).map(s =>
              s.id === 'classify' ? { ...s, status: 'done', elapsedMs: data.elapsedMs } : s
            );
            break;
          case 'llm_start': {
            const loop = data.loop || 1;
            const label = loop === 1 ? 'Thinking' : 'Processing results';
            // Mark previous llm step done if any
            msg.steps = (msg.steps || []).map(s =>
              s.id?.startsWith('llm_') && s.status === 'active' ? { ...s, status: 'done' } : s
            );
            msg.steps.push({ id: `llm_${loop}`, label, status: 'active', startTime: Date.now() });
            break;
          }
          case 'llm_done': {
            const llmElapsed = data.elapsedMs;
            msg.steps = (msg.steps || []).map(s =>
              s.id === `llm_${data.loop}` && s.status === 'active' ? { ...s, status: 'done', elapsedMs: llmElapsed } : s
            );
            break;
          }
          case 'tool_start': {
            // Mark llm step as done
            msg.steps = (msg.steps || []).map(s =>
              s.id?.startsWith('llm_') && s.status === 'active' ? { ...s, status: 'done' } : s
            );
            // Add each tool as a step
            const tools = data.tools || [];
            for (const t of tools) {
              msg.steps.push({ id: `tool_${t.name}_${Date.now()}`, label: t.label || t.name, status: 'active', startTime: Date.now() });
            }
            break;
          }
          case 'tool_done': {
            const elapsed = data.elapsedMs;
            const doneNames = new Set((data.tools || []).map(t => t.name));
            // Mark matching active tool steps as done
            msg.steps = (msg.steps || []).map(s => {
              if (s.status !== 'active') return s;
              // Extract tool name from step id: "tool_{name}_{timestamp}"
              const match = s.id?.match(/^tool_(.+)_\d+$/);
              if (match && doneNames.has(match[1])) {
                return { ...s, status: 'done', elapsedMs: elapsed };
              }
              return s;
            });
            break;
          }
          case 'tool': {
            // Legacy format fallback
            const names = data.names || [data.name];
            // Mark llm step as done
            msg.steps = (msg.steps || []).map(s =>
              s.id?.startsWith('llm_') && s.status === 'active' ? { ...s, status: 'done' } : s
            );
            for (const name of names) {
              msg.steps = [...(msg.steps || [])];
              msg.steps.push({ id: `tool_${name}_${Date.now()}`, label: name, status: 'active', startTime: Date.now() });
            }
            break;
          }
          case 'agent_stream':
            // Live streaming from Claude Code agent
            if (!msg.steps?.find(s => s.id === 'agent')) {
              msg.steps = (msg.steps || []).map(s =>
                s.status === 'active' ? { ...s, status: 'done' } : s
              );
              msg.steps.push({ id: 'agent', label: 'Agent working', status: 'active', startTime: Date.now() });
            }
            msg.parts[2] = data.text;
            msg.text = msg.parts.filter(Boolean).join('\n');
            break;
          case 'llm_delta':
            // Streaming OpenAI Responses completion — accumulated text so far.
            msg.parts[2] = data.accum || '';
            msg.text = msg.parts.filter(Boolean).join('\n');
            break;
          case 'final':
            // Mark all remaining active steps as done
            msg.steps = (msg.steps || []).map(s =>
              s.status === 'active' ? { ...s, status: 'done' } : s
            );
            msg.totalElapsedMs = data.totalElapsedMs;
            msg.parts[1] = '';
            msg.parts[2] = data.text;

            if (data.messageId) {
              msg.backendMessageId = data.messageId;
            }

            if (dialogueId === currentDialogueId) {
              if (data.transactions && data.transactions.length > 0) {
                setPendingTransactions(data.transactions);
                setNewIssueIds(new Set(data.newIssueIds || []));
              } else {
                setPendingTransactions([]);
                setNewIssueIds(new Set());
              }
            }

            if (dialogueId && inProgressMessagesCache.current[dialogueId]) {
              setTimeout(() => {
                if (inProgressMessagesCache.current[dialogueId]) {
                  inProgressMessagesCache.current[dialogueId] =
                    inProgressMessagesCache.current[dialogueId].filter(m => m.id !== msg.id);
                }
              }, 2000);
            }
            break;
          case 'error':
            msg.steps = (msg.steps || []).map(s =>
              s.status === 'active' ? { ...s, status: 'failed' } : s
            );
            msg.parts[1] = '';
            msg.parts[2] = data.message || 'An error occurred. Please try again.';
            if (dialogueId === currentDialogueId) {
              setPendingTransactions([]);
              setNewIssueIds(new Set());
            }

            if (dialogueId && inProgressMessagesCache.current[dialogueId]) {
              setTimeout(() => {
                if (inProgressMessagesCache.current[dialogueId]) {
                  inProgressMessagesCache.current[dialogueId] =
                    inProgressMessagesCache.current[dialogueId].filter(m => m.id !== msg.id);
                }
              }, 2000);
            }
            break;
          case 'command_detect_done':
            msg.steps = (msg.steps || []).map(s =>
              s.id === 'classify' ? { ...s, status: 'done' } : s
            );
            break;
          default:
            break;
        }

        // Build parts[1] from steps for text display (fallback)
        const activeSteps = (msg.steps || []).filter(s => s.status === 'active');
        msg.parts[1] = activeSteps.length > 0 ? activeSteps.map(s => s.label).join(', ') + '...' : '';
        msg.text = msg.parts.filter(Boolean).join('\n');
        copy[idx] = msg;
        return copy;
      });
      
      // Also store progress updates for RecordingModal
      setProgressUpdates(prev => ({
        ...prev,
        [id]: { phase, data }
      }));
    });

    socket.on("dialogue_refresh", ({ dialogueId }) => {
      console.log(`[Socket] dialogue_refresh for ${dialogueId}`);
      if (loadDialogues) loadDialogues();
    });

    return () => socket.disconnect();
  }, [user, currentDialogueId, loadDialogues]);

  // Helper to load dialogue messages and merge any cached in-progress messages
  const loadDialogueMessages = useCallback((dialogueMessages, dialogueId) => {
    if (!Array.isArray(dialogueMessages)) dialogueMessages = [];
    const cachedMessages = inProgressMessagesCache.current[dialogueId] || [];

    if (cachedMessages.length > 0) {
      console.log(`[Dialogue] Merging ${cachedMessages.length} in-progress messages for dialogue ${dialogueId}`);

      // Only merge messages that don't already exist in dialogueMessages
      const existingIds = new Set(dialogueMessages.map(m => m.id));
      const uniqueCachedMessages = cachedMessages.filter(m => !existingIds.has(m.id));

      if (uniqueCachedMessages.length > 0) {
        console.log(`[Dialogue] Adding ${uniqueCachedMessages.length} unique in-progress messages`);
        setMessages([...dialogueMessages, ...uniqueCachedMessages]);

        // Update cache to only keep the unique messages
        inProgressMessagesCache.current[dialogueId] = uniqueCachedMessages;
      } else {
        console.log(`[Dialogue] No unique cached messages to merge, clearing cache`);
        // All cached messages already exist in Firestore, clear the cache
        delete inProgressMessagesCache.current[dialogueId];
        setMessages(dialogueMessages);
      }
    } else {
      setMessages(dialogueMessages);
    }
  }, []);

  const value = {
    messages,
    setMessages,
    loadDialogueMessages,
    pendingTransactions,
    setPendingTransactions,
    newIssueIds,
    setNewIssueIds,
    currentContext,
    setCurrentContext,
    sendAudio,
    commitTransactions,
    cancelTransactions,
    retryUpload,
    progressUpdates,
    socketRef
  };

  return (
    <RecordingContext.Provider value={value}>
      {children}
    </RecordingContext.Provider>
  );
};

export const useRecording = () => {
  const context = useContext(RecordingContext);
  if (!context) {
    // Return safe defaults instead of throwing during unmount/remount transitions
    return {
      messages: [],
      setMessages: () => {},
      loadDialogueMessages: () => {},
      pendingTransactions: [],
      setPendingTransactions: () => {},
      newIssueIds: new Set(),
      setNewIssueIds: () => {},
      currentContext: null,
      setCurrentContext: () => {},
      sendAudio: async () => ({ success: false }),
      commitTransactions: async () => ({ success: false }),
      cancelTransactions: async () => ({ success: false }),
      retryUpload: async () => ({ success: false }),
      progressUpdates: {},
      socketRef: { current: null }
    };
  }
  return context;
};